// Core Modules
import { Route } from '@angular/router';

// External Modules
import { NgxPermissionsObject } from 'ngx-permissions';

// Enums
import { MainNavigationEnum } from '@shared/enum/main-navigation-url.enum';

/**
 * A side-nav destination inside a feature area.
 */
export interface NavDestination {
  /** Visible label in the rail. */
  label: string;
  /** Path relative to the area root; '' is the area's own landing page. */
  path: string;
}

/**
 * A lazily-loaded feature area: one top-level route, one gating permission, N rail destinations.
 */
export interface NavArea {
  /** Side-nav group heading the destinations appear under. */
  group: string;
  /** Top-level route segment. */
  path: MainNavigationEnum;
  /** ngx-permissions name gating the route and every destination in it. */
  permission: string;
  /** Lazy route table for the area. */
  loadChildren: Route['loadChildren'];
  /** Rail entries in display order; the first is this area's landing target. */
  destinations: NavDestination[];
}

/**
 * A navigation target: an absolute route plus the label that names it.
 */
export interface NavTarget {
  /** Visible label, e.g. for a "Go to Clients" button. */
  label: string;
  /** Absolute route path (e.g. `/users`). */
  route: string;
}

/**
 * A labelled, collapsible group of nav targets.
 */
export interface NavGroup {
  /** Group heading. */
  label: string;
  /** Targets in the group, in display order. */
  items: NavTarget[];
}

/**
 * Single source of truth for the app's feature areas. The side-nav rail, the router table
 * (`app.routes.ts`), the post-login landing route, the header Home link and the Access-denied
 * exit button are all derived from this list — adding a screen here is the only edit required.
 *
 * Order is behaviour, not cosmetics: it is both the rail order and the landing priority.
 */
export const NAV_AREAS: readonly NavArea[] = [
  {
    group: 'Access & Identity',
    path: MainNavigationEnum.USERS,
    permission: 'user-read',
    loadChildren: () => import('@features/users/users.routes').then((m) => m.routes),
    destinations: [{ label: 'Users', path: '' }],
  },
  {
    group: 'Access & Identity',
    path: MainNavigationEnum.BULK_ROLE_ASSIGNMENT,
    permission: 'user-write',
    loadChildren: () => import('@features/bulk-role-assignment/bulk-role-assignment.routes').then((m) => m.routes),
    destinations: [{ label: 'Bulk Role Assignment', path: '' }],
  },
  {
    group: 'Access & Identity',
    path: MainNavigationEnum.TOKENS,
    permission: 'token-read',
    loadChildren: () => import('@features/tokens/tokens.routes').then((m) => m.routes),
    destinations: [
      { label: 'API Tokens', path: '' },
      { label: 'Token Expirations', path: 'expirations' },
    ],
  },
  {
    group: 'Clients',
    path: MainNavigationEnum.CLIENTS,
    permission: 'client-read',
    loadChildren: () => import('@features/clients/clients.routes').then((m) => m.routes),
    destinations: [{ label: 'Clients', path: '' }],
  },
  {
    group: 'Activity',
    path: MainNavigationEnum.HISTORICAL_PULL,
    permission: 'historical-pull-read',
    loadChildren: () => import('@features/activity/activity.routes').then((m) => m.routes),
    destinations: [{ label: 'Historical Pulls', path: '' }],
  },
  {
    group: 'Activity',
    path: MainNavigationEnum.SF_STREAMLIT,
    permission: 'sf-streamlit-read',
    loadChildren: () => import('@features/sf-streamlit/sf-streamlit.routes').then((m) => m.routes),
    destinations: [{ label: 'Streamlit Apps', path: '' }],
  },
  {
    group: 'Data Health',
    path: MainNavigationEnum.DATA_HEALTH,
    permission: 'data-health-read',
    loadChildren: () => import('@features/data-health/data-health.routes').then((m) => m.routes),
    destinations: [{ label: 'Data Health Dashboard', path: '' }],
  },
  {
    // One entry for the whole area: Conversations, Chat Feedback and Unanswered Questions are
    // now tabs of a single page, not three destinations. `path: ''` is the bare area path, which
    // `chat-admin.routes.ts` redirects to the first tab; the rail's longest-prefix matching
    // (see `SideNavComponent.activeRoute`) keeps the entry highlighted on every tab and on the
    // detail pages beneath them.
    group: 'Ignite Assistant',
    path: MainNavigationEnum.CHAT_ADMIN,
    permission: 'chat-admin-read',
    loadChildren: () => import('@features/chat-admin/chat-admin.routes').then((m) => m.routes),
    destinations: [{ label: 'Ignite Assistant', path: '' }],
  },
];

/**
 * Absolute route for a destination inside an area.
 * @param area owning area
 * @param destination destination within it
 * @returns absolute route path
 */
const destinationRoute = (area: NavArea, destination: NavDestination): string => (destination.path ? `/${area.path}/${destination.path}` : `/${area.path}`);

/**
 * The rail, reduced to what the given permissions admit. Areas sharing a `group` label are merged
 * into one heading in first-appearance order; groups with no permitted area are dropped.
 * @param permissions current ngx-permissions store
 * @returns labelled groups in display order
 */
export function navGroups(permissions: NgxPermissionsObject): NavGroup[] {
  const groups: NavGroup[] = [];
  for (const area of NAV_AREAS) {
    if (!permissions[area.permission]) continue;
    const items = area.destinations.map((destination) => ({ label: destination.label, route: destinationRoute(area, destination) }));
    const existing = groups.find((group) => group.label === area.group);
    if (existing) {
      existing.items.push(...items);
    } else {
      groups.push({ label: area.group, items });
    }
  }
  return groups;
}

/**
 * First destination the given permissions admit, in registry order. This is the landing page:
 * the rail and the route guards read the same permissions, so landing anywhere else bounces
 * scope-limited users to Access denied. The label comes back too, so a link to it can be
 * labelled ("Go to Clients") without a second lookup that could disagree.
 * @param permissions current ngx-permissions store
 * @returns the target, or null when no destination is permitted
 */
export function firstPermittedDestination(permissions: NgxPermissionsObject): NavTarget | null {
  for (const area of NAV_AREAS) {
    if (permissions[area.permission] && area.destinations.length) {
      const [first] = area.destinations;
      return { label: first.label, route: destinationRoute(area, first) };
    }
  }
  return null;
}

/**
 * The area a URL belongs to, so a permission change can ask whether the user may still be there.
 * @param url absolute URL, with or without query string and fragment
 * @returns the owning area, or undefined for a route outside the registry
 */
export function areaForUrl(url: string): NavArea | undefined {
  const [segment] = url.split(/[?#]/)[0].split('/').filter(Boolean);
  return NAV_AREAS.find((area) => area.path === segment);
}
